import { useState } from "react";
import { Link } from "react-router-dom";
import { LOGO_URL, SERVICES } from "@/data/services";
import { MapPin, Mail, Phone, Send, Facebook, Instagram, Linkedin, Youtube } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const XIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const WhatsAppIcon = ({ size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const SOCIALS = [
  { Icon: Facebook, href: "https://www.facebook.com/tanzbridge", label: "Facebook", hover: "hover:bg-[#1877F2] hover:border-[#1877F2]" },
  { Icon: Instagram, href: "https://www.instagram.com/tanzbridgein/", label: "Instagram", hover: "hover:bg-[#E4405F] hover:border-[#E4405F]" },
  { Icon: XIcon, href: "https://x.com/tanzbridge", label: "X", hover: "hover:bg-black hover:border-black" },
  { Icon: Linkedin, href: "https://www.linkedin.com/company/tanz-bridge-international-ltd/?viewAsMember=true", label: "LinkedIn", hover: "hover:bg-[#0A66C2] hover:border-[#0A66C2]" },
  { Icon: WhatsAppIcon, href: "https://wa.me/13475107195?text=Hello%20Tanz-BRIDGE%20International%20LTD", label: "WhatsApp", hover: "hover:bg-[#25D366] hover:border-[#25D366]" },
  // { Icon: Youtube, href: "https://www.youtube.com/channel/UC87eInTE8HfraVO5mrdavFw", label: "YouTube", hover: "hover:bg-[#FF0000] hover:border-[#FF0000]" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed! Great business tips are on the way.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative bg-brand-dark text-white" data-testid="main-footer">
      <div className="max-w-[1320px] mx-auto px-4 sm:px-6 py-16 grid gap-12 lg:grid-cols-4 text-center lg:text-left">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-3 mb-6 justify-center lg:justify-start">
            <Link to="/" data-testid="footer-logo" onClick={() => window.scrollTo(0, 0)} className="shrink-0">
              <img src={LOGO_URL} alt="Tanz-Bridge — Commitment for Excellence" className="h-16 w-auto object-contain rounded-lg bg-white px-3 py-2" />
            </Link>
          </div>
          <p className="text-base font-medium text-gray-200 max-w-md mx-auto lg:mx-0 leading-relaxed">
            Tanz-Bridge International Ltd is an intermediary company providing technical consultancy
            services in Tanzania and NYC linking producers and consumers on a global market.
          </p>
          <form onSubmit={subscribe} className="mt-6 flex max-w-md mx-auto lg:mx-0 gap-2" data-testid="footer-newsletter-form">
            <input
              data-testid="footer-newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email for business tips"
              className="flex-1 bg-white/10 border border-white/20 rounded-sm px-4 py-3 text-sm text-white placeholder:text-gray-400 focus:outline-none focus:border-brand-yellow transition-colors"
            />
            <button
              data-testid="footer-newsletter-submit"
              disabled={loading}
              className="bg-brand-yellow hover:bg-brand-yellowhover text-brand-dark font-semibold px-4 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-60"
            >
              <Send size={16} /> Join
            </button>
          </form>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase text-brand-yellow mb-5">Services</h4>
          <ul className="space-y-3">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <a href={`/services/${s.id}`} className="text-gray-300 hover:text-white transition-colors text-sm">
                  {s.name}
                </a>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="text-xs tracking-[0.2em] uppercase text-brand-yellow mb-5">Contact</h4>
          <ul className="space-y-4 text-sm text-gray-300">
            <li className="flex gap-3 justify-center lg:justify-start items-start"><MapPin size={18} className="text-brand-yellow shrink-0 mt-0.5" /> <span className="text-center lg:text-left">
              <a href="https://www.google.com/maps/search/?api=1&query=MMH5%2B66C%2C+Mpuguso%2C+Tanzania" target="_blank" rel="noopener noreferrer" className="hover:text-brand-yellow transition-colors" data-testid="footer-location-rungwe">RUNGWE DISTRICT<br />P.O Box 404, TUKUYU</a><br /><br />
              <a href="https://www.google.com/maps/search/?api=1&query=30+Old+Kings+Highway+South%2C+Darien%2C+CT+06820" target="_blank" rel="noopener noreferrer" className="hover:text-brand-yellow transition-colors" data-testid="footer-location-newyork">30 Old Kings Hwy S<br />Darien, CT 06820</a><br /><br />
              <a href="https://www.google.com/maps/search/?api=1&query=Mlandege+Street%2C+Iringa%2C+Tanzania" target="_blank" rel="noopener noreferrer" className="hover:text-brand-yellow transition-colors" data-testid="footer-location-iringa">MLANDEGE STREET<br />P.O Box 504, IRINGA</a>
            </span></li>
            <li className="flex gap-3 justify-center lg:justify-start"><Mail size={18} className="text-brand-yellow shrink-0" /> info@tanzbridge.com</li>
            <li className="flex gap-3 justify-center lg:justify-start"><Phone size={18} className="text-brand-yellow shrink-0" /> <span className="flex flex-col gap-1">
              <a href="tel:+255712196560" className="hover:text-brand-yellow transition-colors" data-testid="footer-phone-1">+255 712 196 560</a>
              <a href="tel:+19433008407" className="hover:text-brand-yellow transition-colors" data-testid="footer-phone-2">+1 (943) 300-8407</a>
              <a href="tel:+255759384302" className="hover:text-brand-yellow transition-colors" data-testid="footer-phone-3">+255 759 384 302</a>
            </span></li>
          </ul>
          <div className="mt-6 flex items-center justify-center lg:justify-start gap-3" data-testid="footer-socials">
            {SOCIALS.map(({ Icon, href, label, hover }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className={`h-9 w-9 rounded-full border border-white/20 bg-white/5 text-gray-200 flex items-center justify-center transition-colors duration-200 hover:text-white ${hover}`}
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div id="footer-bottom-bar" className="max-w-[1320px] mx-auto px-4 sm:px-6 py-6 flex flex-col items-center text-center gap-3 lg:grid lg:grid-cols-4 lg:items-center lg:text-left text-sm font-medium text-gray-300">
          <span className="lg:col-span-2">© {new Date().getFullYear()} Tanz-Bridge International Ltd. All rights reserved.</span>
          {/*
          <span data-testid="footer-macaja">
            Made with <span className="text-brand-yellow">Love</span> by{" "}
            <a href="https://www.magawa.website" target="_blank" rel="noopener noreferrer"
              className="text-green-500 hover:underline font-semibold" data-testid="footer-macaja-link">MaCaja</a>
          </span>
          */}
          <span id="footer-made-in" className="lg:col-span-2 font-semibold lg:text-right">Made in New York, USA</span>
        </div>
      </div>
    </footer>
  );
};
